/**
 * SearchBar Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Main property search interface with date picker, guest selector, and bedroom filter.
 * Responsive design with mobile-optimized sheets and desktop dropdowns.
 * Integrates with SearchBarContext for global state management.
 * 
 * Features:
 * - Date range picker with CalendarModal
 * - Guest selector (adults, children, infants)
 * - Bedroom filter with user override
 * - Flexible date search option
 * - Mobile-optimized UI (sheets instead of dropdowns)
 * - Desktop-optimized UI (dropdowns and inline controls)
 * - Search submission with analytics tracking
 * - European date format (DD/MM/YYYY)
 * - Responsive breakpoint detection (768px)
 * 
 * Dependencies:
 * - React 19.2.4 (hooks, state management)
 * - React Router 7 (navigation)
 * - CalendarModal (date selection)
 * - Font Awesome 6.5 (icons)
 * - SearchBarContext (global search state)
 * - TailwindCSS 4.1 (responsive styling)
 * - Analytics tracking (trackViewSearchResults)
 * 
 * State Management:
 * - activeSection: 'dates' | 'guests' | 'bedrooms' | null
 * - showCalendar: boolean (calendar modal visibility)
 * - isCompact: boolean (mobile vs desktop layout)
 * - showMobileGuestSheet: boolean (mobile guest selector)
 * - showMobileBedroomSheet: boolean (mobile bedroom selector)
 * - bedroomsUserOverride: boolean (user-set bedroom preference)
 * 
 * Search Parameters (from context):
 * - dateRange: [Date, Date]
 * - guests: { adults, children, infants }
 * - bedrooms: number
 * - isFlexible: boolean
 * - flexibleDays: number
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation with mobile/desktop responsive design
 * - Fixed infinite re-render loops via stabilized dependencies
 * - Integrated with SearchBarContext for state management
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarModal } from './CalendarModal';
import { ProIcon, useIsMobile } from './ProIcon';
import { trackViewSearchResults } from '../lib/analytics';
import { useSearchBar } from '../contexts/SearchBarContext';

export const SearchBar = () => {
  const navigate = useNavigate();
  const { searchParams, updateSearchParams } = useSearchBar();
  const [activeSection, setActiveSection] = useState<'dates' | 'guests' | 'bedrooms' | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [showMobileGuestSheet, setShowMobileGuestSheet] = useState(false);
  const [showMobileBedroomSheet, setShowMobileBedroomSheet] = useState(false);
  const [bedroomsUserOverride, setBedroomsUserOverride] = useState(false);
  
  const { dateRange, guests, bedrooms, isFlexible, flexibleDays } = searchParams;
  const [startDate, endDate] = dateRange;
  const totalGuests = guests.adults + guests.children + guests.infants;
  const isMobileViewport = useIsMobile();

  const formatMobileDate = (date: Date) =>
    date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  const formatDesktopDate = (date: Date) =>
    date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsCompact(width < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (activeSection) {
        const target = e.target as Element;
        // Check if click is outside the search bar container
        if (!target.closest('.search-bar-container')) {
          setActiveSection(null);
        }
      }
    };
    
    // Use 'click' instead of 'mousedown' to avoid conflicts with button onClick
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeSection]);

  const handleSearch = () => {
    trackViewSearchResults('marbella rental search', {
      check_in: startDate ? startDate.toISOString().split('T')[0] : undefined,
      check_out: endDate ? endDate.toISOString().split('T')[0] : undefined,
      flexible: isFlexible,
      adults: guests.adults,
      children: guests.children,
      infants: guests.infants,
      guests_total: totalGuests,
      bedrooms,
    });

    // Pass search state to destinations page
    navigate('/destinations', {
      state: {
        startDate,
        endDate,
        isFlexible,
      }
    });
  };

  // Smart bedroom adjustment based on guests (if user hasn't manually set bedrooms)
  useEffect(() => {
    if (!bedroomsUserOverride) {
      // For 4 guests (2 adults + 2 children), default to 2 bedrooms
      // For other guest counts: 1 bedroom per 2 guests (minimum 1)
      const recommendedBedrooms = totalGuests === 4 ? 2 : Math.max(1, Math.ceil(totalGuests / 2));
      updateSearchParams({ bedrooms: recommendedBedrooms });
    } else {
      // If user had manually set bedrooms, check if it's still reasonable
      // Minimum 1 bedroom for every 4 guests
      const minimumBedrooms = Math.max(1, Math.ceil(totalGuests / 4));
      if (bedrooms < minimumBedrooms) {
        // Auto-adjust if their choice is unrealistic
        updateSearchParams({ bedrooms: minimumBedrooms });
      }
    }
  }, [totalGuests, bedroomsUserOverride]);

  return (
    <div className="search-bar-container w-full md:max-w-5xl mx-auto relative z-[50] px-3 sm:px-0">
      {/* Mobile Layout - Stacked rows with bottom sheet pickers */}
      {isCompact ? (
        <div className="ui-frosted-surface rounded-3xl ui-shadow-panel-ocean p-3 space-y-2">
          {/* Row 1: Dates */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowCalendar(true);
            }}
            className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-2xl transition-colors flex items-center gap-3"
          >
            <div className="w-9 h-9 bg-slate-200/70 rounded-xl flex items-center justify-center flex-shrink-0">
              <ProIcon icon="calendar-range" family="solid" className="w-4 h-4 text-slate-600" />
            </div>
            <div className="text-left flex-1 min-w-0">
              <div className="ui-eyebrow-muted">Dates</div>
              <div className="text-sm font-bold text-slate-900 truncate flex items-center gap-2">
                {startDate && endDate
                  ? `${formatMobileDate(startDate)} — ${formatMobileDate(endDate)}`
                  : 'Add Dates'}
                {isFlexible && (
                  <span className="inline-flex items-center gap-1 bg-brand-sun/10 text-brand-sun text-xs font-bold px-1.5 py-0.5 rounded">
                    <ProIcon icon="arrows-left-right" family="solid" className="text-[10px]" />
                    ±{flexibleDays}
                  </span>
                )}
              </div>
            </div>
          </button>

          {/* Row 2: Guests + Bedrooms side by side */}
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMobileGuestSheet(true);
              }}
              className="flex-1 px-3 py-3 bg-slate-50 hover:bg-slate-100 rounded-2xl transition-colors flex items-center gap-2.5 min-w-0"
            >
              <div className="w-8 h-8 bg-slate-200/70 rounded-lg flex items-center justify-center flex-shrink-0">
                <ProIcon icon="family-dress" family="solid" className="w-3.5 h-3.5 text-slate-600" />
              </div>
              <div className="text-left min-w-0">
                <div className="ui-eyebrow-muted">Guests</div>
                <div className="text-sm font-bold text-slate-900">{totalGuests}</div>
              </div>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMobileBedroomSheet(true);
              }}
              className="flex-1 px-3 py-3 bg-slate-50 hover:bg-slate-100 rounded-2xl transition-colors flex items-center gap-2.5 min-w-0"
            >
              <div className="w-8 h-8 bg-slate-200/70 rounded-lg flex items-center justify-center flex-shrink-0">
                <ProIcon icon="bed" family="utility" className="w-3.5 h-3.5 text-slate-600" />
              </div>
              <div className="text-left min-w-0">
                <div className="ui-eyebrow-muted">Bedrooms</div>
                <div className="text-sm font-bold text-slate-900">{bedrooms === 0 ? 'Any' : bedrooms}</div>
              </div>
            </button>
          </div>

          {/* Row 3: Search Button */}
          <button
            onClick={handleSearch}
            className="ui-btn-primary w-full py-3.5 gap-2 shadow-xl shadow-brand-sun/30"
          >
            <ProIcon icon="magnifying-glass" family="utility" className="w-5 h-5 text-white" />
            <span className="text-sm">SEARCH RENTALS</span>
          </button>
        </div>
      ) : (
        /* Desktop Layout */
        <div className="ui-frosted-surface ui-radius-hero ui-shadow-panel-ocean p-2 flex items-center gap-1">
          {/* Dates */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setActiveSection(activeSection === 'dates' ? null : 'dates');
              setShowCalendar(true);
            }}
            className={`flex-shrink-0 px-4 md:px-6 py-4 md:py-5 ui-radius-panel transition-all group min-w-0 ${
              activeSection && activeSection !== 'dates' 
                ? 'opacity-40 hover:opacity-60' 
                : 'hover:bg-slate-50/80'
            }`}
          >
            <div className="flex items-center gap-3 md:gap-4">
              <div className="p-3 md:p-4 bg-slate-100 rounded-2xl group-hover:bg-slate-200 transition-all duration-300 shadow-sm flex-shrink-0">
                  <ProIcon icon="calendar-range" family="solid" className="text-xl text-slate-700 group-hover:text-slate-900 transition-colors" />
              </div>
              <div className="flex flex-col text-left flex-1 min-w-0">
                  <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-0.5">Dates</span>
                <span className="text-sm font-black text-slate-900 truncate flex items-center gap-2">
                  {startDate && endDate ? (
                    `${formatDesktopDate(startDate)} - ${formatDesktopDate(endDate)}`
                  ) : (
                    <span className="text-slate-400">Add Dates</span>
                  )}
                  {isFlexible && (
                    <span className="inline-flex items-center gap-1 bg-brand-sun/10 text-brand-sun text-xs font-bold px-2 py-0.5 rounded">
                      <ProIcon icon="arrows-left-right" family="solid" className="text-[10px]" />
                      ±{flexibleDays}
                    </span>
                  )}
                </span>
              </div>
            </div>
          </button>

          <div className="hidden md:block w-px h-14 bg-slate-100 flex-shrink-0"></div>

          {/* Guests Selection */}
          <div className="flex-1 guest-picker-container relative min-w-0">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setActiveSection(activeSection === 'guests' ? null : 'guests');
              }}
              className={`w-full px-4 md:px-6 py-4 md:py-5 ui-radius-panel transition-all group min-w-0 ${
                activeSection && activeSection !== 'guests' 
                  ? 'opacity-40 hover:opacity-60' 
                  : 'hover:bg-slate-50/80'
              }`}
            >
              <div className="flex items-center gap-3 md:gap-4">
                <div className="p-3 md:p-4 bg-slate-100 rounded-2xl group-hover:bg-slate-200 transition-all duration-300 shadow-sm flex-shrink-0">
                  <ProIcon icon="family-dress" family="solid" className="text-xl text-slate-700 group-hover:text-slate-900 transition-colors" />
                </div>
                <div className="flex flex-col text-left flex-1 min-w-0">
                  <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-0.5">Guests</span>
                  <span className="text-sm font-black text-slate-900 whitespace-nowrap">
                    {totalGuests}<span className="hidden lg:inline"> {totalGuests === 1 ? 'Guest' : 'Guests'}</span>
                  </span>
                </div>
              </div>
            </button>
            
            {activeSection === 'guests' && (
              <div className="absolute top-full left-0 mt-6 w-80 bg-white ui-radius-hero ui-shadow-panel p-10 z-50 border border-slate-100" onClick={(e) => e.stopPropagation()}>
                <div className="space-y-8">
                  <GuestRow label="Adults" sub="Ages 13+" value={guests.adults} onChange={(v) => updateSearchParams({guests: {...guests, adults: v}})} />
                  <GuestRow label="Children" sub="Ages 2-12" value={guests.children} onChange={(v) => updateSearchParams({guests: {...guests, children: v}})} />
                  <GuestRow label="Infants" sub="Under 2" value={guests.infants} onChange={(v) => updateSearchParams({guests: {...guests, infants: v}})} />
                </div>
              </div>
            )}
          </div>

          <div className="hidden md:block w-px h-14 bg-slate-100 flex-shrink-0"></div>

          {/* Bedrooms Selection */}
          <div className="flex-1 bedroom-picker-container relative min-w-0">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setActiveSection(activeSection === 'bedrooms' ? null : 'bedrooms');
              }}
              className={`w-full px-4 md:px-6 py-4 md:py-5 ui-radius-panel transition-all group min-w-0 ${
                activeSection && activeSection !== 'bedrooms' 
                  ? 'opacity-40 hover:opacity-60' 
                  : 'hover:bg-slate-50/80'
              }`}
            >
              <div className="flex items-center gap-3 md:gap-4">
                <div className="p-3 md:p-4 bg-slate-100 rounded-2xl group-hover:bg-slate-200 transition-all duration-300 shadow-sm flex-shrink-0">
                  <ProIcon icon="bed" family="solid" className="text-xl text-slate-700 group-hover:text-slate-900 transition-colors" />
                </div>
                <div className="flex flex-col text-left flex-1 min-w-0">
                  <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-0.5">Bedrooms</span>
                  <span className="text-sm font-black text-slate-900 whitespace-nowrap">
                    {bedrooms === 0 ? <span className="text-slate-400">Any</span> : `${bedrooms}`}<span className="hidden lg:inline"> {bedrooms === 1 ? 'Bedroom' : 'Beds'}</span>
                  </span>
                </div>
              </div>
            </button>
            
            {activeSection === 'bedrooms' && (
              <div className="absolute top-full left-0 mt-6 w-80 bg-white ui-radius-hero ui-shadow-panel p-10 z-50 border border-slate-100" onClick={(e) => e.stopPropagation()}>
                <div className="space-y-8">
                  <GuestRow 
                    label="Bedrooms" 
                    sub="Number of bedrooms" 
                    value={bedrooms} 
                    onChange={(v) => {
                      updateSearchParams({bedrooms: v});
                    }} 
                  />
                </div>
              </div>
            )}
          </div>

          <div className="hidden md:block w-px h-14 bg-slate-100 flex-shrink-0"></div>

          {/* Search Button */}
          <button 
            onClick={handleSearch}
            className="ui-btn-primary px-6 lg:px-10 py-4 md:py-6 ui-radius-panel gap-2 md:gap-3 shadow-2xl shadow-brand-sun/30 group flex-shrink-0"
          >
            <ProIcon icon="magnifying-glass" family="utility" className="text-xl group-hover:scale-110 transition-transform text-white" />
            <span className="hidden md:inline">SEARCH</span>
          </button>
        </div>
      )}

      {/* Mobile Guest Bottom Sheet */}
      {isCompact && showMobileGuestSheet && (
        <div className="fixed inset-0 z-[200]">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowMobileGuestSheet(false)} />
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl p-6 pb-[calc(2rem+env(safe-area-inset-bottom,0px))] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mb-5" />
            <h3 className="text-lg font-bold text-slate-900 mb-4">Guests</h3>
            <div className="space-y-6">
              <GuestRow label="Adults" sub="Ages 13+" value={guests.adults} onChange={(v) => updateSearchParams({guests: {...guests, adults: v}})} />
              <GuestRow label="Children" sub="Ages 2-12" value={guests.children} onChange={(v) => updateSearchParams({guests: {...guests, children: v}})} />
              <GuestRow label="Infants" sub="Under 2" value={guests.infants} onChange={(v) => updateSearchParams({guests: {...guests, infants: v}})} />
            </div>
            <button
              onClick={() => setShowMobileGuestSheet(false)}
              className="ui-btn-ocean w-full mt-6 py-3.5"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Mobile Bedroom Bottom Sheet */}
      {isCompact && showMobileBedroomSheet && (
        <div className="fixed inset-0 z-[200]">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowMobileBedroomSheet(false)} />
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl p-6 pb-[calc(2rem+env(safe-area-inset-bottom,0px))] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mb-5" />
            <h3 className="text-lg font-bold text-slate-900 mb-4">Bedrooms</h3>
            <div className="space-y-6">
              <GuestRow 
                label="Bedrooms" 
                sub="Number of bedrooms" 
                value={bedrooms} 
                onChange={(v) => {
                  updateSearchParams({bedrooms: v});
                  setBedroomsUserOverride(true);
                }}
              />
            </div>
            <button
              onClick={() => setShowMobileBedroomSheet(false)}
              className="ui-btn-ocean w-full mt-6 py-3.5"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Calendar Modal */}
      <CalendarModal
        isOpen={showCalendar}
        startDate={startDate}
        endDate={endDate}
        onClose={() => setShowCalendar(false)}
        onDatesChange={(dates, flexible = false, flexDays = 0) => {
          updateSearchParams({dateRange: dates, isFlexible: flexible || false, flexibleDays: flexDays});
        }}
        initialFlexibleDays={flexibleDays}
      />
    </div>
  );
};

const GuestRow = ({ label, sub, value, onChange }: { label: string, sub: string, value: number, onChange: (v: number) => void }) => (
  <div className="flex justify-between items-center">
    <div>
      <div className="text-sm font-black text-slate-900 uppercase tracking-tight">{label}</div>
      <div className="ui-eyebrow-subtle">{sub}</div>
    </div>
    <div className="flex items-center gap-4">
      <button 
        className="ui-icon-toggle border-slate-100 disabled:opacity-20"
        onClick={() => onChange(Math.max(0, value - 1))}
        disabled={value === 0}
      >
        <ProIcon icon="minus" family="utility" className="text-xs" />
      </button>
      <span className="w-6 text-center text-lg font-black text-slate-900">{value}</span>
      <button 
        className="ui-icon-toggle border-slate-100"
        onClick={() => onChange(value + 1)}
      >
        <ProIcon icon="plus" family="utility" className="text-xs" />
      </button>
    </div>
  </div>
);
